import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Award } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
export function MasterClassSection() {
  const {
    t,
    language
  } = useLanguage();
  const galleryImages = [{
    src: 'https://cdn.magicpatterns.com/uploads/3Ciy9izwsWWr9uLMegooec/lore_master_class.jpg',
    alt: 'Lorena Master Class — Graduadas con certificados'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/cS3rU2yyiSeoZctoN8tD7t/lor_emaster_class_2.jpg',
    alt: 'Lorena Master Class — Alumnas certificadas'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/now785CfL7DdC3ZhBFKzT3/lore_master_class5.jpg',
    alt: 'Lorena Master Class — Sesiones de formación'
  }];
  return <section id="formacion" className="pt-4 pb-20 bg-beige-light" data-id="element-230">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-231">
        {/* Hero Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-10" data-id="element-232">
          <div className="flex items-center justify-center space-x-3 mb-4" data-id="element-233">
            <GraduationCap className="w-5 h-5 text-gold" data-id="element-234" />
            <span className="text-sm font-medium text-gold uppercase tracking-widest" data-id="element-235">
              {t('masterclass', 'badge')}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-dark mb-6" data-id="element-236">
            {t('masterclass', 'title')}
          </h2>
          <div className="w-24 h-1 bg-gold mx-auto mb-6" data-id="element-237"></div>
          <p className="text-lg text-dark/70 max-w-2xl mx-auto leading-relaxed" data-id="element-238">
            {t('masterclass', 'desc')}
          </p>
        </motion.div>

        {/* Master Class Hero Image */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.7
      }} className="relative max-w-3xl mx-auto mb-12 group" data-id="element-239">
          {/* Decorative gold frame */}
          <div className="absolute -inset-3 border-2 border-gold/30 rounded-lg" data-id="element-240"></div>
          <div className="absolute -inset-1.5 border border-gold/15 rounded-md" data-id="element-241"></div>
          <div className="relative overflow-hidden rounded-md shadow-2xl bg-beige-light" data-id="element-242">
            <img src="https://cdn.magicpatterns.com/uploads/qJN3tjiGP5b52cBW99PNwR/master_class_nuevo.png" alt="Lorena Beauty House — Master Class" className="w-full h-auto object-contain" data-id="element-243" />
            {/* Subtle gold shimmer overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-tr from-gold/0 via-gold/5 to-gold/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700" data-id="element-244"></div>
          </div>
          {/* Gold corner accents - larger for premium feel */}
          <div className="absolute -top-3 -left-3 w-10 h-10 border-t-2 border-l-2 border-gold/0 group-hover:border-gold transition-all duration-500 rounded-tl-lg" data-id="element-245"></div>
          <div className="absolute -top-3 -right-3 w-10 h-10 border-t-2 border-r-2 border-gold/0 group-hover:border-gold transition-all duration-500 rounded-tr-lg" data-id="element-246"></div>
          <div className="absolute -bottom-3 -left-3 w-10 h-10 border-b-2 border-l-2 border-gold/0 group-hover:border-gold transition-all duration-500 rounded-bl-lg" data-id="element-247"></div>
          <div className="absolute -bottom-3 -right-3 w-10 h-10 border-b-2 border-r-2 border-gold/0 group-hover:border-gold transition-all duration-500 rounded-br-lg" data-id="element-248"></div>
        </motion.div>

        {/* Highlights */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="flex flex-wrap justify-center gap-4 mb-12" data-id="element-249">
          {['highlight1', 'highlight2', 'highlight3', 'highlight4'].map((key, i) => <div key={i} className="flex items-center space-x-2 bg-white px-5 py-3 rounded-full border border-gold/20 shadow-sm" data-id="element-250">
                <Award className="w-4 h-4 text-gold" data-id="element-251" />
                <span className="text-sm font-medium text-dark" data-id="element-252">
                  {t('masterclass', key)}
                </span>
              </div>)}
        </motion.div>

        {/* Gallery Grid — same style as Facilities */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5" data-id="element-253">
          {galleryImages.map((image, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.6,
          delay: index * 0.15
        }} className="relative overflow-hidden rounded-md group aspect-square border-2 border-gold/20 shadow-md hover:shadow-xl hover:border-gold/50 transition-all duration-500" data-id="element-254">
              <img src={image.src} alt={image.alt} className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-110" data-id="element-255" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-256"></div>
              {/* Gold corner accents */}
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-gold/0 group-hover:border-gold transition-all duration-500 rounded-tl-md" data-id="element-257"></div>
              <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-gold/0 group-hover:border-gold transition-all duration-500 rounded-tr-md" data-id="element-258"></div>
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-gold/0 group-hover:border-gold transition-all duration-500 rounded-bl-md" data-id="element-259"></div>
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-gold/0 group-hover:border-gold transition-all duration-500 rounded-br-md" data-id="element-260"></div>
            </motion.div>)}
        </div>

        <motion.p initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="text-center mt-10" data-id="element-261">
          <p className="text-gold-dark font-serif text-xl font-medium italic mb-6" data-id="element-262">
            {t('masterclass', 'cta')}
          </p>
          <a href="https://book.stripe.com/14A3cucxT2QIgIWdwB3cc01" target="_blank" rel="noopener noreferrer" className="inline-block bg-gold hover:bg-gold-dark text-white px-10 py-4 rounded-sm font-bold text-lg transition-colors shadow-lg uppercase tracking-wide" data-id="element-263">
            {language === 'es' ? 'Inscríbete Ahora' : 'Enroll Now'}
          </a>
        </motion.p>
      </div>
    </section>;
}